import { EventBus } from '@/plugins/event-bus'
import DealerAccessories from 'api-client/DealerAccessory.api'
import loadAccessoriesAction from '@/helpers/enums/loadAccessoriesAction'

const moduleState = {
  all: [],
  selectedProducts: [],
  loading: true,
  loadFailed: false,
}

const getters = {
  all: (state) => state.all,
  selectedProducts: (state) => state.selectedProducts,
  loading: (state) => state.loading,
  loadFailed: (state) => state.loadFailed,
  accessoriesWithCategories: (state) => (categories) => {
    return state.all.filter((p) => categories.includes(p.Category))
  },
  getMatchingAccessory: (state) => (id) => {
    // Try to find matching accessories and return them
    const product = state.all.find((product) => {
      return product.OptionType_ID === id
    })
    return product ? product : null
  },
}

const mutations = {
  /* eslint-disable no-param-reassign */
  setAll: (state, payload) => {
    state.all = payload
    EventBus.$emit('accessories/all')
    EventBus.$emit('accessories/accessoriesWithCategories')
  },
  reset: (state) => {
    state.all = []
  },
  setLoadingState: (state, payload) => {
    state.loading = payload
    EventBus.$emit('accessories/loading')
  },
  setLoadFailed: (state, payload) => {
    state.loadFailed = payload
    EventBus.$emit('accessories/loadFailed')
  },
}

const actions = {
  setAll: ({ commit }, accessories) => {
    commit('setAll', accessories || [])
    commit('setLoadFailed', false)
    commit('setLoadingState', false)
  },
  reset: ({ commit }) => {
    commit('reset')
    commit('setLoadFailed', false)
    commit('setLoadingState', false)
  },
  async initForStockcar({ commit, dispatch }, args) {
    if (args.stockCarId === undefined) return

    commit('setLoadFailed', false)
    try {
      const accessories = await DealerAccessories.getForStockCarId(args.stockCarId)
      dispatch('setAll', accessories)

      const standardAccessories = accessories?.filter((a) => a.Standard)
      dispatch('quote/setAccessories', standardAccessories, { root: true })
    } catch {
      commit('setLoadFailed', true)
    } finally {
      commit('setLoadingState', false)
    }
  },
  async initForConfigurationCode({ commit, dispatch }, args) {
    if (args.configurationCode === undefined) return

    commit('setLoadFailed', false)
    try {
      const accessories = await DealerAccessories.getForConfigurationCode(args.configurationCode)
      dispatch('setAll', accessories)

      const standardAccessories = accessories?.filter((a) => a.Standard)
      dispatch('quote/setAccessories', standardAccessories, { root: true })
    } catch {
      commit('setLoadFailed', true)
    } finally {
      commit('setLoadingState', false)
    }
  },
  async initForStock({ commit, dispatch }, args) {
    if (args.stockId === undefined) return

    commit('setLoadFailed', false)
    try {
      const accessories = await DealerAccessories.getForStockId(args.stockId)
      dispatch('setAll', accessories)

      const standardAccessories = accessories?.filter((a) => a.Standard)
      dispatch('quote/setAccessories', standardAccessories, { root: true })
    } catch {
      commit('setLoadFailed', true)
    } finally {
      commit('setLoadingState', false)
    }
  },
  async getForConfigurationCode({ commit, dispatch }, args) {
    if (args.configurationCode === undefined) return

    commit('setLoadFailed', false)
    try {
      const accessories = await DealerAccessories.getForConfigurationCode(args.configurationCode)
      return await dispatch('updateAccessories', {
        accessories,
        checkPrices: false,
        loadAccessories: loadAccessoriesAction.keep,
      })
    } catch {
      commit('setLoadFailed', true)
    } finally {
      commit('setLoadingState', false)
    }
  },
  async getForStockCar({ commit, dispatch }, args) {
    if (args.stockCarId === undefined) return

    commit('setLoadFailed', false)
    try {
      const accessories = await DealerAccessories.getForStockCarId(args.stockCarId)
      return await dispatch('updateAccessories', {
        accessories,
        checkPrices: false,
        loadAccessories: loadAccessoriesAction.keep,
      })
    } catch {
      commit('setLoadFailed', true)
    } finally {
      commit('setLoadingState', false)
    }
  },
  async getForStock({ commit, dispatch }, args) {
    if (args.stockId === undefined) return

    commit('setLoadFailed', false)
    try {
      const accessories = await DealerAccessories.getForStockId(args.stockId)
      return await dispatch('updateAccessories', {
        accessories,
        checkPrices: false,
        loadAccessories: loadAccessoriesAction.keep,
      })
    } catch {
      commit('setLoadFailed', true)
    } finally {
      commit('setLoadingState', false)
    }
  },

  /// called when loading a quote, setting the accessories. Don't check prices since dealers might have
  /// done that consciously.
  async updateAccessories({ commit, dispatch, rootGetters }, args) {
    const { accessories, checkPrices, loadAccessories, configurationCode } = args

    try {
      if (accessories == null) return

      dispatch('setAll', accessories)

      // get accessories that where previously selected (and are still available)
      const accessoriesOnQuote = rootGetters['quote/accessories']
      if (loadAccessories === loadAccessoriesAction.overwrite) {
        await dispatch('initForConfigurationCode', { configurationCode })
      } else {
        let validAccessories
        if (loadAccessories === loadAccessoriesAction.renew)
          validAccessories = functions.getValidNewAccessories(accessoriesOnQuote, accessories)
        else
          validAccessories = functions.getValidCurrentAccessories(accessoriesOnQuote, accessories)

        if (checkPrices && loadAccessories === loadAccessoriesAction.renew) {
          // compare prices with those previously on the quote
          if (functions.accessoriesPricesHaveChanged(validAccessories, accessoriesOnQuote))
            EventBus.$emit('notification-warning', 'DealerAccessoriesPricesChanged')
        } else if (checkPrices && loadAccessories === loadAccessoriesAction.keep) {
          // compare prices with those from allAccessories
          if (functions.accessoriesPricesHaveChanged(validAccessories, accessories))
            EventBus.$emit('notification-warning', 'DealerAccessoriesPricesChanged')
        }
        if (functions.accessoriesOptionTypeIDsHaveChanged(accessoriesOnQuote, validAccessories))
          EventBus.$emit('notification-warning', 'DealerAccessoriesUpdated')

        return await dispatch('quote/setAccessories', validAccessories, { root: true })
      }
    } finally {
      commit('setLoadingState', false)
    }
  },
}

const functions = {
  accessoriesOptionTypeIDsHaveChanged(arr1, arr2) {
    if (arr1.length !== arr2.length) return true

    // create a new array with just the OptionTypeID
    const arrA = arr1.map((a) => a.OptionType_ID)
    const arrB = arr2.map((a) => a.OptionType_ID)
    // check if any array contains elements that is not in the other
    const result = arrA
      .filter((x) => !arrB.includes(x))
      .concat(arrB.filter((x) => !arrA.includes(x)))

    return result.length > 0
  },
  accessoriesPricesHaveChanged(accessoriesOnQuote, allAccessories) {
    for (let i = 0; i < accessoriesOnQuote.length; i++) {
      let accessory = accessoriesOnQuote[i]

      // ignore customer corrections
      if (accessory.Category == 'Corrections') continue

      const loadedAccessory = allAccessories.find((a) => a.OptionType_ID == accessory.OptionType_ID)

      if (accessory.AccessorySource == 'SHOPPING_CART' && !loadedAccessory) continue

      if (Number(loadedAccessory.Price.Excl) != Number(accessory.Price.Excl)) return true
    }
    return false
  },
  /**
   * returns an array of accessories which are on the quote and are still valid.
   * Prices that were on the quote remain
   * @param {*} accessoriesOnQuote
   * @param {*} allAccessories
   */
  getValidCurrentAccessories(accessoriesOnQuote, allAccessories) {
    let validAccessories = accessoriesOnQuote
      .filter((a) => !a.IsPCQMGeneratedId && a.AccessorySource != 'SHOPPING_CART')
      .filter((a) => allAccessories.some((b) => a.OptionType_ID == b.OptionType_ID))
    validAccessories = validAccessories.concat(
      accessoriesOnQuote.filter((a) => a.IsPCQMGeneratedId || a.AccessorySource == 'SHOPPING_CART')
    )

    return validAccessories
  },
  /**
   * returns an array of accessories from the new accessories list, which match
   * the accessories on the quote. So it gives you the new accessories, with new prices etc
   * @param {*} accessoriesOnQuote
   * @param {*} allAccessories
   */
  getValidNewAccessories(accessoriesOnQuote, allAccessories) {
    // create an array of accessories, EXCLUDING custom made & corrections, that are both on the quote and
    // in the 'allAccessories' array
    let validAccessories = allAccessories.filter((a) =>
      accessoriesOnQuote
        .filter(
          (a) =>
            !a.IsPCQMGeneratedId &&
            a.AccessorySource != 'SHOPPING_CART' &&
            a.Category != 'Corrections'
        )
        .some((b) => a.OptionType_ID == b.OptionType_ID)
    )

    // add the custom & correction accessories
    validAccessories = validAccessories.concat(
      accessoriesOnQuote.filter(
        (a) =>
          a.IsPCQMGeneratedId || a.AccessorySource == 'SHOPPING_CART' || a.Category == 'Corrections'
      )
    )

    return validAccessories
  },
}

export default {
  state: moduleState,
  getters,
  mutations,
  actions,
  functions,
  namespaced: true,
}
